/* eslint-disable @typescript-eslint/no-use-before-define */
import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import { Typography } from '@euler/app/components/typography';
import { MemberCell } from '@euler/app/flows/common/components/MemberCell';
import { MemberPickerPresentationRequest } from '@euler/app/types';
import { Center } from '@euler/components/Center';
import { FontFamily } from '@euler/components/typography';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useGeneralBottomSheetModal } from '@euler/functions/useBottomSheetModal';
import { useMemberList } from '@euler/functions/useMemberList';
import { OrgMember } from '@euler/model/entity';
import { OrgUserRoleType } from '@euler/model/enum';
import {
  useBehaviorSubject,
  useCreation,
  usePersistFn,
} from '@euler/utils/hooks';
import { getPyInitials } from '@euler/utils/pinyin/getPyInitials';
import {
  BottomSheetFlatList,
  BottomSheetModal,
  BottomSheetModalProvider,
} from '@gorhom/bottom-sheet';
import {
  ReactNode,
  Suspense,
  createContext,
  memo,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  FlatList,
  Platform,
  StyleProp,
  TextInput,
  View,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BehaviorSubject } from 'rxjs';

const kMinTechnicianCountThreshold = __DEV__ ? 1 : 5;
const kSearchInputHeight = 44;

const MemberPickerBottomSheetContext = createContext<{
  memberPickerPresentationRequest$: BehaviorSubject<
    MemberPickerPresentationRequest | undefined
  >;
}>(null as any);

export const useMemberPickerBottomSheetContext = () =>
  useContext(MemberPickerBottomSheetContext);

export const MemberPickerBottomSheetProvider = memo(
  ({ children }: { children?: ReactNode }) => {
    const memberPickerPresentationRequest$ = useCreation(
      () =>
        new BehaviorSubject<MemberPickerPresentationRequest | undefined>(
          undefined,
        ),
      [],
    );

    const [memberPickerPresentationRequest] = useBehaviorSubject(
      memberPickerPresentationRequest$,
    );

    const onMemberPickerDismiss = usePersistFn(() => {
      memberPickerPresentationRequest$.value?.onDismiss?.();
      memberPickerPresentationRequest$.next(undefined);
    });

    return (
      <BottomSheetModalProvider>
        <MemberPickerBottomSheetContext.Provider
          value={{
            memberPickerPresentationRequest$,
          }}
        >
          {children}
          <MemberPickerBottomSheet
            show={memberPickerPresentationRequest != null}
            title={memberPickerPresentationRequest?.title}
            subTitle={memberPickerPresentationRequest?.subTitle}
            role={memberPickerPresentationRequest?.role}
            onSelect={memberPickerPresentationRequest?.onSelect}
            onDismiss={onMemberPickerDismiss}
          />
        </MemberPickerBottomSheetContext.Provider>
      </BottomSheetModalProvider>
    );
  },
);

export const MemberPickerBottomSheet = memo(
  ({
    show,
    title,
    role,
    subTitle,
    onSelect,
    onDismiss,
  }: {
    show?: boolean;
    title?: string;
    role?: OrgUserRoleType | OrgUserRoleType[];
    subTitle?: ReactNode;
    onSelect?: (member: OrgMember) => void;
    onDismiss?: () => void;
  }) => {
    const { bottomSheetModalRef, ...bottomSheetProps } =
      useGeneralBottomSheetModal({
        show,
        mediumSnapPoint: '75%',
        onDismiss,
      });

    const onKeywordInputFocus = usePersistFn(() => {
      const n = (bottomSheetProps.snapPoints as any[]).length;
      bottomSheetModalRef.current?.snapToIndex(n - 1);
    });

    return (
      <BottomSheetModal ref={bottomSheetModalRef} {...bottomSheetProps}>
        <Suspense
          fallback={
            <Content
              loading={true}
              members={[]}
              title={title}
              subTitle={subTitle}
              role={role}
              contentContainerStyle={{ flexGrow: 1, alignItems: 'center' }}
            />
          }
        >
          <WithMemberList
            title={title}
            subTitle={subTitle}
            role={role}
            onSelect={onSelect}
            onKeywordInputFocus={onKeywordInputFocus}
          />
        </Suspense>
      </BottomSheetModal>
    );
  },
);

type ContentProps = {
  title?: string;
  subTitle?: ReactNode;
  role?: OrgUserRoleType | OrgUserRoleType[];
  onSelect?: (member: OrgMember) => void;
  onKeywordInputFocus?: () => void;
};

const WithMemberList = memo((props: ContentProps) => {
  const members = useMemberList()!;
  return <Content {...props} members={members} />;
});

const Content = memo(
  ({
    loading,
    title,
    subTitle,
    members,
    role = OrgUserRoleType.Technicians,
    contentContainerStyle,
    onSelect,
    onKeywordInputFocus,
  }: ContentProps & {
    loading?: boolean;
    contentContainerStyle?: StyleProp<ViewStyle>;
    members: OrgMember[];
  }) => {
    const { rpx } = useResponsiveContext();
    const roles = useMemo(() => (Array.isArray(role) ? role : [role]), [role]);
    const insets = useSafeAreaInsets();
    const [keyword, setKeyword] = useState<string>('');
    const originalTechnicianList = useMemo(
      () =>
        members
          .filter(x => new OrgUserRoleHelper(x.role).matchesAny(roles))
          .map(x => ({ ...x, pyInitial: getPyInitials(x.name) })),
      [members, roles],
    );

    const technicians = useMemo(
      () =>
        originalTechnicianList.filter(x => {
          const word = keyword.trim();
          return !word || x.pyInitial.includes(word) || x.name.includes(word);
        }),
      [originalTechnicianList, keyword],
    );

    const showTitle = title != null;
    const showSearch =
      originalTechnicianList.length > kMinTechnicianCountThreshold;

    // BottomSheet specific scroll view components do not work on Web right now.
    // see https://github.com/gorhom/react-native-bottom-sheet/issues/11.
    const ListComponent =
      Platform.OS === 'web' ? FlatList : BottomSheetFlatList;

    const ListHeader =
      showTitle || showSearch
        ? () => (
            <View
              css={`
                padding: ${rpx(10)}px 0;
                background-color: #fff;
              `}
            >
              {title != null && (
                <Center
                  css={`
                    margin-bottom: ${showSearch ? rpx(10) : 0}px;
                  `}
                >
                  <Typography.Label size={rpx(18)} lineHeight={rpx(30)}>
                    {title}
                  </Typography.Label>
                </Center>
              )}
              {subTitle ? (
                <View
                  css={`
                    margin-bottom: ${rpx(10)}px;
                  `}
                >
                  {subTitle}
                </View>
              ) : null}
              {showSearch ? (
                <View
                  css={`
                    background-color: #fff;
                    height: ${rpx(kSearchInputHeight)}px;
                    border-radius: ${rpx(kSearchInputHeight) / 2}px;
                    border-color: #ddd;
                    border-width: 1px;
                    margin: 0 ${rpx(20)}px;
                    padding: 0 ${rpx(10)}px;
                  `}
                >
                  <TextInput
                    placeholder="输入关键词或拼音首字母搜索"
                    value={keyword ?? ''}
                    onChangeText={setKeyword}
                    onFocus={onKeywordInputFocus}
                    clearButtonMode={keyword.trim() ? 'always' : 'never'}
                    css={`
                      flex: 1;
                      font-family: ${FontFamily.NotoSans.Regular};
                      font-size: ${rpx(16)}px;
                      color: #000;
                      padding: 0 ${rpx(10)}px;
                    `}
                  />
                </View>
              ) : null}
            </View>
          )
        : undefined;

    return (
      <ListComponent
        data={technicians}
        keyExtractor={x => String(x.id)}
        ListHeaderComponent={ListHeader}
        stickyHeaderIndices={[0]}
        contentContainerStyle={[
          {
            paddingLeft: rpx(12),
            paddingRight: rpx(12),
            paddingBottom: insets.bottom + rpx(15),
          },
          contentContainerStyle,
        ]}
        renderItem={({ item, index }) => (
          <MemberCell
            member={item}
            first={index === 0}
            last={index === members.length - 1}
            onPress={member => onSelect?.(member)}
            isRoleLabelVisible
          />
        )}
        ListEmptyComponent={
          loading ? (
            <View>
              <DefaultLoader />
            </View>
          ) : undefined
        }
      />
    );
  },
);
